import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Database, ArrowRight } from "lucide-react";
import dayjs from "dayjs";

import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { ModuleTitleCard, ContainerCard } from "../../components/ui/cards";
import { EMPLOYEE_MIGRATION_LIST } from "../../constants/api";
import { BRANCH_FILTER_CHANGED_EVENT } from "../../utils/branchSession";
import MasterMigrationLinks from "./MasterMigrationLinks";
import { getMigrationSampleUrl } from "./masterMigrationConfigs";
import { migrationBranchFilter, migrationBranchLabel, migrationBranchRequired } from "./migrationBranch";

const STATUS_STYLES = {
    completed: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
    pending: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300",
};

const isAdmin = () =>
    JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
    JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

const EmployeeMigration = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const admin = useMemo(isAdmin, []);
    const [branchFilter, setBranchFilter] = useState(() => migrationBranchFilter());
    const branchName = migrationBranchLabel();

    useEffect(() => {
        const sync = () => setBranchFilter(migrationBranchFilter());
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, sync);
        return () => window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, sync);
    }, []);

    // Resume an existing batch: completed -> RM allocation, otherwise the
    // review grid. The wizard re-derives state from the id, so this is just a hint.
    const openBatch = (record) =>
        navigate(`/datamigration/employeemigration/batch/${record.id}`, {
            state: { status: record.status },
        });

    const columns = useMemo(
        () => [
            {
                accessor: "migration_date_time",
                Header: "Migrated Date & Time",
                sortable: true,
                Cell: ({ value }) =>
                    value ? dayjs(value).format("DD MMM YYYY • hh:mm A") : "-",
            },
            {
                accessor: "migrated_by",
                Header: "Migrated By",
                Cell: ({ value }) => value || "-",
            },
            {
                accessor: "count",
                Header: "Records",
                Cell: ({ value }) => `${value || 0}/100`,
            },
            {
                accessor: "status",
                Header: "Status",
                filterable: true,
                Cell: ({ value }) => {
                    const cls = STATUS_STYLES[value] ||
                        "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
                    return (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>
                            {value || "-"}
                        </span>
                    );
                },
            },
            {
                accessor: "actions",
                Header: "Action",
                Cell: ({ row }) => (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7"
                        onClick={() => openBatch(row)}
                    >
                        {row.status === "completed" ? "View" : "Resume"}
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                ),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    if (!admin) {
        return (
            <div>
                <ModuleTitleCard Icon={Database} title="Employee Migration" />
                <ContainerCard>
                    <p className="text-sm text-muted-foreground p-4">
                        You are not authorized to access employee data migration.
                    </p>
                </ContainerCard>
            </div>
        );
    }

    const branchId = branchFilter.branch;
    const needsBranch = migrationBranchRequired();

    return (
        <div>
            <ModuleTitleCard
                Icon={Database}
                title="Employee Migration"
                subtitle={
                    branchName
                        ? `Bulk-import employees for ${branchName} only. Migrated employees are assigned to this branch.`
                        : "Select a branch before starting a migration. Each batch and migrated employee belongs to that branch only."
                }
                totalRecords={totalRecords}
                buttonEnable={!needsBranch}
                buttonName="New Migration"
                setOpenDialog={() => {
                    if (needsBranch) return;
                    navigate("/datamigration/employeemigration/new");
                }}
            />

            {needsBranch && (
                <div className="mx-1 mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                    Select a branch from the header or branch picker before creating or viewing migrations.
                </div>
            )}

            {/* <div className="px-1 pb-2">
                <a
                    href={getMigrationSampleUrl("employee")}
                    download
                    className="text-sm text-sidebar-primary hover:underline"
                >
                    Download employee migration sample CSV (5 sample employees)
                </a>
            </div> */}

            <ContainerCard>
                <Reactable
                    pagename="employee-migration"
                    columns={columns}
                    data={data}
                    setData={setData}
                    setTotalCount={setTotalRecords}
                    url={EMPLOYEE_MIGRATION_LIST}
                    filter={branchId ? { branch: branchId } : {}}
                    serverSide
                    isCSVRequired
                />
            </ContainerCard>

            <MasterMigrationLinks />
        </div>
    );
};

export default EmployeeMigration;
